import React, { useState, useEffect } from 'react';
import { Store, MapPin, Phone, Globe, Mail, Clock, Edit3, Save, X, Camera, Star } from 'lucide-react';
import { getProfileUpdate, PutBusinessProfile } from '../../services/api';
import { useTheme } from '../../contexts/ThemeContext';
import toast from 'react-hot-toast';

const BusinessProfile = () => {
  const { theme } = useTheme();
  const [profileData, setProfileData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [selectedShop, setSelectedShop] = useState(0);

  useEffect(() => {
    fetchProfileData();
  }, []);

  const fetchProfileData = async () => {
    try {
      setLoading(true);
      const resp = await getProfileUpdate({});
      const data = resp?.extractedData || {};
      setProfileData(data);
      const shopsArr = Array.isArray(data.shopDetails) ? data.shopDetails : (Array.isArray(data.shops) ? data.shops : []);
      if (shopsArr.length > 0) {
        setEditForm(shopsArr[0]);
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
      toast.error('Failed to load profile data');
    } finally {
      setLoading(false);
    }
  };

  const handleEditToggle = () => {
    if (editing) {
      setEditForm(profileData.shops[selectedShop]);
    }
    setEditing(!editing);
  };

  const handleSave = async () => {
    try {
      const existing = Array.isArray(profileData.shopDetails) ? profileData.shopDetails : (Array.isArray(profileData.shops) ? profileData.shops : []);
      const updatedShops = [...existing];
      updatedShops[selectedShop] = editForm;

      // Call API to persist changes using PUT with document _id in URL
      const docId = profileData?._id || profileData?.id || profileData?.profile_id;
      const payload = {
        shopDetails: updatedShops,
      };
      await PutBusinessProfile(docId, payload);

      toast.success('Profile updated successfully!');
      setEditing(false);
      // Reflect locally using canonical key 'shopDetails'
      setProfileData({
        ...profileData,
        shopDetails: updatedShops,
      });
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error('Failed to update profile');
    }
  };

  const handleInputChange = (field, value) => {
    setEditForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleOfferChange = (offerIndex, field, value) => {
    const updatedOffers = [...(editForm.offers || [])];
    updatedOffers[offerIndex] = {
      ...updatedOffers[offerIndex],
      [field]: value
    };
    setEditForm(prev => ({
      ...prev,
      offers: updatedOffers
    }));
  };

  const addOffer = () => {
    const newOffer = {
      title: '',
      description: '',
      discountValue: 0,
      discountType: 'percentage',
      expiry: '30 days',
      status: 'active'
    };
    setEditForm(prev => ({
      ...prev,
      offers: [...(prev.offers || []), newOffer]
    }));
  };

  const removeOffer = (offerIndex) => {
    const updatedOffers = editForm.offers.filter((_, index) => index !== offerIndex);
    setEditForm(prev => ({
      ...prev,
      offers: updatedOffers
    }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading profile...</p>
        </div>
      </div>
    );
  }

  if (!profileData || !profileData.shops || profileData.shops.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Store className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-700 mb-2">No Profile Data</h2>
          <p className="text-gray-500">Unable to load business profile information.</p>
        </div>
      </div>
    );
  }

  const currentShop = (Array.isArray(profileData.shopDetails) ? profileData.shopDetails : profileData.shops || [])[selectedShop] || {};

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className={`${theme.gradient} shadow-lg`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-6">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
                <Store className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-white">Business Profile</h1>
                <p className="text-white text-opacity-80">Manage your business information</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-4">
              {editing ? (
                <>
                  <button
                    onClick={handleSave}
                    className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors"
                  >
                    <Save className="w-4 h-4" />
                    <span>Save Changes</span>
                  </button>
                  <button
                    onClick={handleEditToggle}
                    className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors"
                  >
                    <X className="w-4 h-4" />
                    <span>Cancel</span>
                  </button>
                </>
              ) : (
                <button
                  onClick={handleEditToggle}
                  className="bg-white bg-opacity-20 hover:bg-opacity-30 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors"
                >
                  <Edit3 className="w-4 h-4" />
                  <span>Edit Profile</span>
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Shop Selection */}
        {profileData.shops.length > 1 && (
          <div className="mb-6">
            <div className="flex space-x-2">
              {profileData.shops.map((shop, index) => (
                <button
                  key={index}
                  onClick={() => {
                    setSelectedShop(index);
                    setEditForm(shop);
                  }}
                  className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                    selectedShop === index
                      ? 'bg-blue-600 text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  {shop.name || `Shop ${index + 1}`}
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Basic Information */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
              <h2 className="text-xl font-semibold text-gray-800 mb-6">Basic Information</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Business Name
                  </label>
                  {editing ? (
                    <input
                      type="text"
                      value={editForm.name || ''}
                      onChange={(e) => handleInputChange('name', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  ) : (
                    <p className="text-gray-900 font-medium">{currentShop.name}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Category
                  </label>
                  {editing ? (
                    <select
                      value={editForm.category || ''}
                      onChange={(e) => handleInputChange('category', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="">Select Category</option>
                      <option value="restaurant">Restaurant</option>
                      <option value="retail">Retail</option>
                      <option value="service">Service</option>
                      <option value="entertainment">Entertainment</option>
                      <option value="health">Health & Wellness</option>
                      <option value="beauty">Beauty & Spa</option>
                    </select>
                  ) : (
                    <p className="text-gray-900 capitalize">{currentShop.category}</p>
                  )}
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description
                  </label>
                  {editing ? (
                    <textarea
                      value={editForm.description || ''}
                      onChange={(e) => handleInputChange('description', e.target.value)}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Describe your business..."
                    />
                  ) : (
                    <p className="text-gray-700">{currentShop.description || 'No description available'}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Contact Information */}
            <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
              <h2 className="text-xl font-semibold text-gray-800 mb-6">Contact Information</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Phone className="w-4 h-4 inline mr-2" />
                    Phone Number
                  </label>
                  {editing ? (
                    <input
                      type="tel"
                      value={editForm.phone || ''}
                      onChange={(e) => handleInputChange('phone', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  ) : (
                    <p className="text-gray-900">{currentShop.phone || 'Not provided'}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Mail className="w-4 h-4 inline mr-2" />
                    Email
                  </label>
                  {editing ? (
                    <input
                      type="email"
                      value={editForm.email || ''}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  ) : (
                    <p className="text-gray-900">{currentShop.email || 'Not provided'}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Globe className="w-4 h-4 inline mr-2" />
                    Website
                  </label>
                  {editing ? (
                    <input
                      type="url"
                      value={editForm.website || ''}
                      onChange={(e) => handleInputChange('website', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="https://..."
                    />
                  ) : (
                    <p className="text-gray-900">
                      {currentShop.website ? (
                        <a href={currentShop.website} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                          {currentShop.website}
                        </a>
                      ) : (
                        'Not provided'
                      )}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <MapPin className="w-4 h-4 inline mr-2" />
                    Address
                  </label>
                  {editing ? (
                    <textarea
                      value={editForm.address || ''}
                      onChange={(e) => handleInputChange('address', e.target.value)}
                      rows={2}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Enter full address..."
                    />
                  ) : (
                    <p className="text-gray-900">{currentShop.address || 'Not provided'}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Business Hours */}
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h2 className="text-xl font-semibold text-gray-800 mb-6">
                <Clock className="w-5 h-5 inline mr-2" />
                Business Hours
              </h2>
              
              <div className="space-y-4">
                {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map((day) => (
                  <div key={day} className="flex items-center justify-between">
                    <span className="font-medium text-gray-700 w-24">{day}</span>
                    {editing ? (
                      <div className="flex items-center space-x-2">
                        <input
                          type="time"
                          value={editForm.hours?.[day.toLowerCase()]?.open || '09:00'}
                          onChange={(e) => handleInputChange('hours', {
                            ...editForm.hours,
                            [day.toLowerCase()]: {
                              ...editForm.hours?.[day.toLowerCase()],
                              open: e.target.value
                            }
                          })}
                          className="px-2 py-1 border border-gray-300 rounded text-sm"
                        />
                        <span>to</span>
                        <input
                          type="time"
                          value={editForm.hours?.[day.toLowerCase()]?.close || '18:00'}
                          onChange={(e) => handleInputChange('hours', {
                            ...editForm.hours,
                            [day.toLowerCase()]: {
                              ...editForm.hours?.[day.toLowerCase()],
                              close: e.target.value
                            }
                          })}
                          className="px-2 py-1 border border-gray-300 rounded text-sm"
                        />
                      </div>
                    ) : (
                      <span className="text-gray-600">
                        {currentShop.hours?.[day.toLowerCase()] 
                          ? `${currentShop.hours[day.toLowerCase()].open} - ${currentShop.hours[day.toLowerCase()].close}`
                          : '9:00 AM - 6:00 PM'
                        }
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Shop Stats */}
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Shop Statistics</h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Total Offers</span>
                  <span className="font-semibold text-blue-600">{currentShop.offers?.length || 0}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Rating</span>
                  <div className="flex items-center space-x-1">
                    <Star className="w-4 h-4 text-yellow-400 fill-current" />
                    <span className="font-semibold">{currentShop.rating || '4.5'}</span>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Distance</span>
                  <span className="font-semibold">{currentShop.distance || '0.5 km'}</span>
                </div>
              </div>
            </div>

            {/* Current Offers */}
            <div className="bg-white rounded-xl shadow-sm p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-800">Current Offers</h3>
                {editing && (
                  <button
                    onClick={addOffer}
                    className="text-blue-600 hover:text-blue-700 text-sm font-medium"
                  >
                    + Add Offer
                  </button>
                )}
              </div>
              
              <div className="space-y-3">
                {(editing ? editForm.offers : currentShop.offers)?.map((offer, index) => (
                  <div key={index} className="border border-gray-200 rounded-lg p-3">
                    {editing ? (
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <input
                            type="text"
                            value={offer.title || ''}
                            onChange={(e) => handleOfferChange(index, 'title', e.target.value)}
                            placeholder="Offer title"
                            className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm"
                          />
                          <button
                            onClick={() => removeOffer(index)}
                            className="text-red-500 hover:text-red-700 ml-2"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                        <textarea
                          value={offer.description || ''}
                          onChange={(e) => handleOfferChange(index, 'description', e.target.value)}
                          placeholder="Offer description"
                          rows={2}
                          className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                        />
                        <div className="flex space-x-2">
                          <input
                            type="number"
                            value={offer.discountValue || ''}
                            onChange={(e) => handleOfferChange(index, 'discountValue', e.target.value)}
                            placeholder="Discount"
                            className="w-20 px-2 py-1 border border-gray-300 rounded text-sm"
                          />
                          <select
                            value={offer.discountType || 'percentage'}
                            onChange={(e) => handleOfferChange(index, 'discountType', e.target.value)}
                            className="px-2 py-1 border border-gray-300 rounded text-sm"
                          >
                            <option value="percentage">%</option>
                            <option value="fixed">₹</option>
                          </select>
                        </div>
                      </div>
                    ) : (
                      <div>
                        <h4 className="font-medium text-gray-800 text-sm">{offer.title}</h4>
                        <p className="text-gray-600 text-xs mt-1">{offer.description}</p>
                        <div className="flex items-center justify-between mt-2">
                          <span className="text-green-600 font-semibold text-sm">
                            {offer.discountValue}{offer.discountType === 'percentage' ? '%' : '₹'} OFF
                          </span>
                          <span className="text-gray-500 text-xs">{offer.expiry}</span>
                        </div>
                      </div>
                    )}
                  </div>
                )) || (
                  <p className="text-gray-500 text-sm text-center py-4">No offers available</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BusinessProfile;
